import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Send } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import axios from 'axios';
import Header from '../components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// NOTE: This is Google's test key that only works on localhost
// Get your own keys from: https://www.google.com/recaptcha/admin/create
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';
const FEEDBACK_EMAIL = 'admin@mytestcase.com';

export default function Feedback() {
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);
  const [issueType, setIssueType] = useState('General Feedback');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('Low');
  const [email, setEmail] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!recaptchaToken) {
      toast.error('Please complete the reCAPTCHA');
      return;
    }

    if (description.length > 1000) {
      toast.error('Description must be under 1000 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/feedback`, {
        issue_type: issueType,
        description,
        severity,
        email: email || null,
        recaptchaToken
      });

      toast.success('Feedback submitted successfully!');
      setDescription('');
      setEmail('');
      setRecaptchaToken('');
      
      // Reset reCAPTCHA using ref
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="pt-32 pb-20 px-6">
        <div className="max-w-2xl mx-auto">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="mb-8"
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded-lg p-8"
          >
            <h1 className="text-3xl font-heading font-bold mb-2">Feedback</h1>
            <p className="text-muted-foreground mb-8">
              Help us improve! Please report bugs, request new features, or share general feedback by emailing us at{' '}
              <a href={`mailto:${FEEDBACK_EMAIL}`} className="text-blue-500 font-medium hover:underline">{FEEDBACK_EMAIL}</a>.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Issue Type - Hidden for now, will be unhidden later */}
              {/* <div>
                <Label htmlFor="issue-type">Issue Type *</Label>
                <Select value={issueType} onValueChange={setIssueType}>
                  <SelectTrigger className="mt-1 bg-black border-input" data-testid="issue-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bug">Bug</SelectItem>
                    <SelectItem value="Feature Request">Feature Request</SelectItem>
                    <SelectItem value="General Feedback">General Feedback</SelectItem>
                  </SelectContent>
                </Select>
              </div> */}

              {/* Severity - Hidden for now, will be unhidden later */}
              {/* <div>
                <Label htmlFor="severity">Severity *</Label>
                <Select value={severity} onValueChange={setSeverity}>
                  <SelectTrigger className="mt-1 bg-black border-input" data-testid="severity-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div> */}

              {/* Description - Hidden for now, will be unhidden later */}
              {/* <div>
                <Label htmlFor="description">Description * ({description.length}/1000)</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="mt-1 bg-black border-input min-h-[150px]"
                  placeholder="Please describe the issue or feedback in detail..."
                  maxLength={1000}
                  required
                  data-testid="description-textarea"
                />
              </div> */}

              {/* Email - Hidden for now, will be unhidden later */}
              {/* <div>
                <Label htmlFor="email">Email (optional for follow-up)</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 bg-black border-input"
                  placeholder="your.email@example.com"
                  data-testid="email-input"
                />
              </div> */}

              {/* reCAPTCHA - Disabled for now, will be enabled later */}
              {/* <div>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={(token) => setRecaptchaToken(token)}
                  onErrored={() => {
                    console.error('reCAPTCHA error occurred');
                    toast.error('reCAPTCHA failed to load. Please refresh the page.');
                  }}
                  onExpired={() => {
                    setRecaptchaToken('');
                    toast.warning('reCAPTCHA expired. Please verify again.');
                  }}
                  theme="dark"
                  data-testid="recaptcha"
                />
                {RECAPTCHA_SITE_KEY === '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI' && (
                  <p className="text-xs text-yellow-500 mt-2">
                    ⚠️ Using test reCAPTCHA key (localhost only)
                  </p>
                )}
              </div> */}

              {/* Submit Button - Hidden for now, will be unhidden later */}
              {/* <Button
                type="submit"
                className="w-full bg-white text-black hover:bg-gray-200 font-medium"
                disabled={loading || !recaptchaToken}
                data-testid="submit-feedback-button"
              >
                <Send className="h-4 w-4 mr-2" />
                {loading ? 'Submitting...' : 'Submit Feedback'}
              </Button> */}
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
}